/**
 * Long Vowels / Short Vowels — two OPTIONAL Letter/Sound tests (v3.73.0).
 *
 * They sit under the Letter/Sound assessment alongside Uppercase / Lowercase /
 * Sounds: same page (the /assessment test picker), same `assessments` doc
 * shape (testCategory letter_knowledge, quarter-stamped, status /
 * manualEntry semantics unchanged, `totalLetters` = item count — the repo's
 * historically named total field), same Results / Report Card / CSV surfaces.
 * There is no grade gate: any class may give either test, and a teacher may
 * skip either or both — an un-administered vowel test simply has no doc, the
 * same as an un-administered Sounds test.
 *
 * Ten items = the five vowels × two sounds, generic by construction; the
 * teacher prompt lines are original. Provenance:
 * docs/assessments/ITEM_PROVENANCE.md (2026-09-09 addendum).
 *
 * Everything vowel-specific that a surface needs lives here so the three
 * sibling tests stay untouched — items, scoring, storage. The `vowelTests`
 * feature flag (src/constants/featureFlags.js, default ON) is a kill switch
 * only: with it OFF every helper below that enumerates the two tests returns
 * nothing, so steps, columns, rows, CSV rows, and group options vanish and the
 * Letter/Sound page reads exactly as it did in v3.72.0.
 */

import { isFeatureEnabled } from './featureFlags';

export const LONG_VOWELS = 'long_vowels';
export const SHORT_VOWELS = 'short_vowels';

/** Administration order (also the picker order): long first — the paper
 *  protocol asks the long sound first. */
export const VOWEL_TEST_TYPES = [LONG_VOWELS, SHORT_VOWELS];

export const VOWEL_ITEMS_PER_TEST = 5;

export const VOWEL_TEST_LABELS = {
  [LONG_VOWELS]: 'Long Vowels',
  [SHORT_VOWELS]: 'Short Vowels',
};

/** Short column/heading form used by the Results summary table. */
export const VOWEL_TEST_SHORT_LABELS = {
  [LONG_VOWELS]: 'Long Vowels',
  [SHORT_VOWELS]: 'Short Vowels',
};

/**
 * The item banks. Five items each, alphabetical, ids fixed. `letter` is the
 * item's vowel (lowercase — the key the item id, the response map and the
 * said-letter record are built from); the student-facing flashcard shows
 * BOTH forms of it via vowelCardText ("A a"); `sound` / `example` are the
 * teacher-facing expected answer.
 */
export const VOWEL_ITEMS = {
  [LONG_VOWELS]: [
    { id: 'a_long', letter: 'a', sound: '/ā/', example: 'says its name' },
    { id: 'e_long', letter: 'e', sound: '/ē/', example: 'says its name' },
    { id: 'i_long', letter: 'i', sound: '/ī/', example: 'says its name' },
    { id: 'o_long', letter: 'o', sound: '/ō/', example: 'says its name' },
    { id: 'u_long', letter: 'u', sound: '/ū/', example: 'as in use; accept /oo/ as in flute' },
  ],
  [SHORT_VOWELS]: [
    { id: 'a_short', letter: 'a', sound: '/ă/', example: 'apple' },
    { id: 'e_short', letter: 'e', sound: '/ĕ/', example: 'egg' },
    { id: 'i_short', letter: 'i', sound: '/ĭ/', example: 'igloo' },
    { id: 'o_short', letter: 'o', sound: '/ŏ/', example: 'octopus' },
    { id: 'u_short', letter: 'u', sound: '/ŭ/', example: 'up' },
  ],
};

/** Teacher prompt lines — original text, shipped verbatim. */
export const VOWEL_PROMPTS = {
  [LONG_VOWELS]: {
    prompt: 'Tell me the long sound of this letter — the sound that says its name.',
    scoringNote: "The letter's name IS the long sound. Mark it correct.",
  },
  [SHORT_VOWELS]: {
    prompt: 'Tell me the short sound of this letter.',
    scoringNote: "The letter's name is NOT the short sound.",
  },
};

export const VOWEL_DISCONTINUE_RULE =
  'If the child gives no sound for three letters in a row, say: Look at all of these and tell me the ones you do know — and mark the rest not administered.';

/**
 * Per-item "not administered" response value. Mirrors the value the
 * Phonemic Awareness screener stores (utils/paScreener.js
 * RESP_NOT_ADMINISTERED) — same string on purpose so any future shared
 * reader treats both the same — but declared here so the vowel tests never
 * import from the PA module. Never counted as incorrect anywhere.
 */
export const RESP_NOT_ADMINISTERED = 'NotAdministered';

// ─── Bands ───────────────────────────────────────────────────────────────────
//
// Each test is banded on its own 5-point score. The app's Sounds rule
// (Dashboard: Mastery 22–26 / Emerging 12–21 / Needs Support 0–11 on 26)
// scaled to 5 points gives Mastery ≥ 4.23 → 5, Emerging ≥ 2.31 → 3–4,
// Needs Support 0–2 — i.e. exactly the 5 / 3–4 / 0–2 default. Computed at
// display time like every standard assessment; never persisted on the doc.
export const VOWEL_BANDS = [
  { id: 'mastery', label: 'Mastery', min: 5, max: 5 },
  { id: 'emerging', label: 'Emerging', min: 3, max: 4 },
  { id: 'needs_support', label: 'Needs Support', min: 0, max: 2 },
];

/** Band for a 0–5 score, or null for empty / the -1 N/A sentinel. */
export function vowelBandForScore(score) {
  if (score === '' || score === null || score === undefined) return null;
  const n = Number(score);
  if (!Number.isFinite(n) || n < 0) return null; // includes correctCount: -1 (N/A)
  return VOWEL_BANDS.find((b) => n >= b.min && n <= b.max) || null;
}

/** The standard score-cell class for a vowel score (the Results palette). */
export function vowelScoreColorClass(score) {
  if (score === '' || score === null || score === undefined) return '';
  if (Number(score) === -1) return 'score-na';
  const band = vowelBandForScore(score);
  if (!band) return '';
  if (band.id === 'mastery') return 'score-high';
  if (band.id === 'emerging') return 'score-mid';
  return 'score-low';
}

// ─── Identity helpers ────────────────────────────────────────────────────────

export function isVowelTestType(testType) {
  return VOWEL_TEST_TYPES.includes(testType);
}

/** The five student-facing glyphs for a vowel test, in item order. */
export function vowelLetters(testType) {
  return (VOWEL_ITEMS[testType] || []).map((item) => item.letter);
}

/**
 * What the STUDENT sees on a vowel card: both forms of the item's vowel,
 * capital first, one space between — "A a". Display only: the item letter,
 * its id, the key handling and the said-letter record all keep the bare
 * lowercase vowel. The three letter tests never call this — their cards show
 * the one glyph in `testLetters`.
 */
export function vowelCardText(letter) {
  const l = String(letter);
  return `${l.toUpperCase()} ${l.toLowerCase()}`;
}

/** The five response-map keys (item ids) for a vowel test, in item order. */
export function vowelItemIds(testType) {
  return (VOWEL_ITEMS[testType] || []).map((item) => item.id);
}

/** Response-map key for the vowel shown: 'a' on long_vowels → 'a_long'. */
export function vowelItemKey(testType, letter) {
  const item = (VOWEL_ITEMS[testType] || []).find((it) => it.letter === String(letter).toLowerCase());
  return item ? item.id : null;
}

export function vowelItemById(id) {
  for (const testType of VOWEL_TEST_TYPES) {
    const item = VOWEL_ITEMS[testType].find((it) => it.id === id);
    if (item) return { ...item, testType };
  }
  return null;
}

/** Human label for an item id: 'a_long' → 'a — long', 'e_short' → 'e — short'. */
export function vowelItemLabel(id) {
  const m = /^([aeiou])_(long|short)$/.exec(String(id));
  return m ? `${m[1]} — ${m[2]}` : String(id);
}

export function isVowelItemKey(key) {
  return /^[aeiou]_(long|short)$/.test(String(key));
}

// ─── Scoring ─────────────────────────────────────────────────────────────────

/** Count of correct responses. Not-administered items never count. */
export function scoreVowelResponses(responses = {}) {
  return Object.values(responses).filter((r) => r === 'Correct').length;
}

/** Items marked not administered (never counted as incorrect). */
export function vowelNotAdministeredCount(responses = {}) {
  return Object.values(responses).filter((r) => r === RESP_NOT_ADMINISTERED).length;
}

// ─── Kill switch ─────────────────────────────────────────────────────────────
//
// Evaluated at CALL time (not module load) so the test suites can toggle the
// flag the way the repo's other flag-visibility suites do.

export function vowelTestsEnabled() {
  return isFeatureEnabled('vowelTests');
}

/** The vowel testTypes a surface should enumerate — [] when the flag is off. */
export function enabledVowelTestTypes() {
  return vowelTestsEnabled() ? [...VOWEL_TEST_TYPES] : [];
}

/** { long_vowels: 'Long Vowels', ... } — {} when the flag is off. */
export function enabledVowelTestLabels() {
  return vowelTestsEnabled() ? { ...VOWEL_TEST_LABELS } : {};
}
